/**
 * useQuery hook for the paginated, filtered story list in the Story List view.
 *
 * Replaces `storyListEffects.getStories` (compose-app). Mirrors the same
 * `POST /api/data/objects/stories/v1/graphql` request:
 * - Uses `getStoriesInFolder` query when a specific folder is selected.
 * - Uses `getStories` query otherwise (entity-wide story list).
 * Both queries run through `postQuery` (cookies/session auth — non-admin safe)
 * and the response is shaped by `buildPaginationOutput`.
 *
 * Auth note (§1): Story List is non-admin reachable → `postQuery`, not
 * `useGraphQLQuery`.
 *
 * The retired effect dispatched `storyListActionCreators.getAllStoriesSuccess` /
 * `setStoryList` into compose-app state. Callers now consume the hook's `data`
 * directly and do not need those action creators.
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import { DEFAULT_PER_PAGE } from '@opengov/stories-shared/constants';

import { postQuery } from '../../../effects/publications/postQuery';
import {
  getStories,
  getStoriesInFolder,
} from '../../../graphQL/queries/listQueries';
import { buildPaginationOutput } from '../../../transformations/listTransformation';
import { orderBy } from '../../../utils';
import { useToast } from '../../Toast/context/useToast.context';
import { IGetStoryGraphqlResponse, IStoryListing } from '../../../../types';
import { EStoryLists } from '../../../../types/storyListTypes';
import { storyListQueryKeys, StoriesKeyParams } from './storyListQueryKeys';

export type UseStoriesParams = StoriesKeyParams | null;

export function useStories(params: UseStoriesParams, errorMessage: string) {
  const { toggleToast } = useToast();

  // Determine which query to run so we can include it in the query key.
  const isFolderSelected =
    params?.folderId &&
    params.folderId !== EStoryLists.Stories &&
    params.folderId !== EStoryLists.Templates &&
    params.folderId !== EStoryLists.Publications &&
    params.folderId !== EStoryLists.Analytics;

  return useQuery<IStoryListing, Error>({
    queryKey: params
      ? storyListQueryKeys.stories(params)
      : ([...storyListQueryKeys.all, 'stories', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const {
        entityId,
        page,
        perPage,
        sort,
        title,
        storyVisibility,
        ogId,
        shouldFilterByUser,
        folderId,
        showAllStoriesForSelectedFolder,
      } = params;

      let variables: Record<string, unknown> = {
        state: 'published',
        limit: showAllStoriesForSelectedFolder ? null : perPage || DEFAULT_PER_PAGE,
        offset: showAllStoriesForSelectedFolder
          ? null
          : (page - 1) * (perPage || DEFAULT_PER_PAGE),
        entityId,
      };

      if (storyVisibility && storyVisibility !== 'all') {
        variables = { ...variables, state: storyVisibility };
      } else if (storyVisibility === 'all') {
        delete variables.state;
      }

      if (sort) {
        variables = { ...variables, order: orderBy(sort) };
      }
      if (title) {
        variables = { ...variables, title: `%${title}%` };
      }
      if (shouldFilterByUser && ogId) {
        variables = { ...variables, opengovId: ogId };
      }
      if (isFolderSelected) {
        variables = { ...variables, folder: folderId };
      }

      const query = isFolderSelected ? getStoriesInFolder : getStories;
      const response = await postQuery(query, variables);
      const graphqlResponse: IGetStoryGraphqlResponse[] = response.resources;
      const count = response.count.aggregate.count;
      return buildPaginationOutput(page, count, graphqlResponse);
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
